import "whatwg-fetch";
import React from "react";
import ReactDOM from "react-dom";
import deepForceUpdate from "react-deep-force-update";
import queryString from "query-string";
import Lodash from "lodash";
import IsJS from "is_js";
import Mitt from "mitt";
import Breakpoint from "bootstrap-breakpoints";
import UAParser from "ua-parser-js";
import Bowser from "bowser";
import createFetch from '~/utils/createFetch';
import mixpanel from "mixpanel-browser";
import Raven from "raven-js";
import { soundManager } from "soundmanager2";
import device from "current-device";
import firebase from '@firebase/app';
import '@firebase/auth';
import '@firebase/database';
import '@firebase/storage';
import 'lazysizes';
import App from "~/components/App";
import configureStore from "~/store/configureStore";
import history from "~/utils/history";
import { updateMeta } from "~/utils/DOM";
import router from "~/utils/router";
import assetPath from "~/utils/assetPath";
import { isDesktopApp, getParams, isGamedistributionApp } from "~/utils/misc";

require('perfect-scrollbar/jquery')($);

// -----------------------------------------------------------------------------
// Import globally available variables
// -----------------------------------------------------------------------------
// Set main domain for cross origin reasons
document.domain = window.location.hostname;

// Setup Sentry
window.Raven = Raven;
window.Raven.config(
  "https://f22227374ade4cb49a081665ca90a4dd@sentry.io/1303413"
).install();

// Setup event emitter
window.emitter = Mitt();

// Require lodash
window._ = Lodash;

// Setup IsJS
window.is = IsJS;

// Setup bowser
window.bowser = Bowser;

window.device = device;
window.parser = new UAParser(window.navigator.userAgent)

// Initialize breakpoint
Breakpoint.init({
  xs: { min: 0, max: 575 },
  sm: { min: 576, max: 885 },
  md: { min: 886, max: 1199 },
  lg: { min: 1200, max: 1363 },
  xl: { min: 1364, max: Infinity }
});
window.Breakpoint = Breakpoint;

// Listen for height change
let windowResizeTimer;
window.addEventListener("resize", () => {
  // Reset window resize timer
  if (is.existy(windowResizeTimer)) {
    clearTimeout(windowResizeTimer);
  }

  // Call window resize change
  windowResizeTimer = _.delay(() => {
    // Emit event
    emitter.emit("windowResizeEvent");
  }, 320);
});

// Setup query
window.queryParameters = getParams(window.location.search);

// Set game
window.game = "jigsaw"

// Send message to renderer if desktop app
// Listen for Electron action taps
if (isDesktopApp() && is.existy(window.electron)) {
  const query = window._.reduce(window._.omit(window.queryParameters, ["platform", "version", "purchase_id"]), (result, value, key) => {
    if (result == "") {
      result += `${key}=${value}`
    } else {
      result += `&${key}=${value}`
    }
    return result;
  }, "")
  const path = `${window.location.pathname}?${query}`;
  window.electron.sendMessageFromWebviewToRenderer({
    action: "last_path_loaded",
    value: path
  });
}

// Setup Mixpanel
window.mixpanel = mixpanel;
window.mixpanel.init("f1ec6ab319bc48134daf31f7e070c3d1");
window.mixpanel.register({
  platform: document.body.dataset.platform,
  game_version: document.body.dataset.version,
  game: document.body.dataset.game,
  source: document.body.dataset.source
});

// Setup Firebase
window.firebase = firebase;
window.firebase.initializeApp({
  apiKey: "AIzaSyAN4wJagvddtahSqjtjPNokVqugYQLQL2I",
  authDomain: "jigsaw-puzzle-ff8eb.firebaseapp.com",
  databaseURL: "https://jigsaw-puzzle-ff8eb.firebaseio.com",
  projectId: "jigsaw-puzzle-ff8eb",
  storageBucket: "jigsaw-puzzle-ff8eb.appspot.com",
  messagingSenderId: "184963400284"
});

// Listen for auth state
window.firebase.auth().onAuthStateChanged(firebaseUser => {
  // Sign user in anonymously
  if (window.is.not.existy(firebaseUser)) {
    window.firebase.auth().signInAnonymously()
  } else {
    window.firebase.database().ref(".info/connected").on('value', snapshot => {
      if (snapshot.val()) {
        window.firebase.database().ref(`users/${firebaseUser.uid}/jigsaws_played_available_force`).onDisconnect().set(true);
      }
    });
  }
});

// Load gamedistribution code
if (isGamedistributionApp()) {
  window.gamedistributionLoaded = false

  window["GD_OPTIONS"] = {
    "gameId": "4d1a4fc412a84cd6a3b1cc79c79f8200",
    "onEvent": (event) => {
      switch (event.name) {
        case "SDK_READY":
          window.gamedistributionLoaded = true;
          emitter.emit("gamedistributionLoaded");
          break;
        case "SDK_GAME_START":
          $("#score_container .left_menu, #board_container .card_component, .modal_wrapper").show();
          break;
        case "SDK_GAME_PAUSE":
          $("#score_container .left_menu, #board_container .card_component, .modal_wrapper").hide();
          break;
        case "SDK_GDPR_TRACKING":
          // this event is triggered when your user doesn't want to be tracked
          break;
        case "SDK_GDPR_TARGETING":
          // this event is triggered when your user doesn't want personalised targeting
          break;
      }
    },
  };
  (function(d, s, id) {
    var js, fjs = d.getElementsByTagName(s)[0];
    if (d.getElementById(id)) return;
    js = d.createElement(s);
    js.id = id;
    js.src = 'https://html5.api.gamedistribution.com/main.min.js';
    fjs.parentNode.insertBefore(js, fjs);
  }(document, 'script', 'gamedistribution-jssdk'));
}

// Setup sound
window.soundHash = {};
soundManager.setup({
  debugMode: false,
  onready: () => {
    window.soundHash.invalid = soundManager.createSound({
      url: assetPath("invalid", "mp3"),
      autoLoad: true,
      autoPlay: false
    });
    window.soundHash.plop = soundManager.createSound({
      url: assetPath("plop", "mp3"),
      autoLoad: true,
      autoPlay: false
    });
  },
  defaultOptions: {
    volume: 100,
    useHighPerformance: true
  }
});

// Setup notifications
let setupNotifyDefaultsTimer;
const setupNotifyDefaults = () => {
  if (is.existy($.notifyDefaults)) {
    // Set basic values
    let offset = 16;
    let spacing = 8;

    // Change card size
    if (Breakpoint.is("xs") || Breakpoint.is("sm")) {
      offset = 12;
      spacing = 6;
    } else if (Breakpoint.is("xl") && is.above($(window).height(), 850)) {
      offset = 24;
      spacing = 12;
    }

    // Extra offset if desktop
    if (isDesktopApp()) {
      offset += 20;
    }

    // Setup defaults
    $.notifyDefaults({
      element: "body",
      position: "fixed",
      type: "info",
      allow_dismiss: false,
      newest_on_top: true,
      placement: {
        from: "top",
        align: "center"
      },
      offset,
      spacing,
      z_index: 400000,
      delay: 3200,
      timer: 400,
      mouse_over: "pause",
      animate: {
        enter: "animated slideInDown",
        exit: "animated slideOutUp"
      },
      template:
        '<div data-notify="container" class="col-10 col-sm-6 alert alert-{0}" role="alert">' +
        '<button type="button" aria-hidden="true" class="close" data-notify="dismiss">×</button>' +
        '<span data-notify="icon"></span> ' +
        '<span data-notify="title">{1}</span> ' +
        '<span data-notify="message">{2}</span>' +
        '<div class="progress" data-notify="progressbar">' +
        '<div class="progress-bar progress-bar-{0}" role="progressbar" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100" style="width: 0%;"></div>' +
        "</div>" +
        '<a href="{3}" target="{4}" data-notify="url"></a>' +
        "</div>"
    });
    clearTimeout(setupNotifyDefaultsTimer);
  }
}
setupNotifyDefaultsTimer = setInterval(setupNotifyDefaults, 320);
setupNotifyDefaults();

/* eslint-disable window-require */

// window (context) variables that can be easily accessed from any React component
// https://facebook.github.io/react/docs/context.html
const context = {
  // Enables critical path CSS rendering
  // https://github.com/kriasoft/isomorphic-style-loader
  insertCss: (...styles) => {
    // eslint-disable-next-line no-underscore-dangle
    const removeCss = styles.map(x => x._insertCss());
    return () => {
      removeCss.forEach(f => f());
    };
  },
  // Initialize a new Redux store
  // http://redux.js.org/docs/basics/UsageWithReact.html
  store: configureStore(window.App.state, { history }),
  // Universal HTTP client
  fetch: createFetch(fetch, {
    baseUrl: window.App.apiUrl,
  }),
};

// Switch off the native scroll restoration behavior and handle it manually
// https://developers.google.com/web/updates/2015/09/history-api-scroll-restoration
const scrollPositionsHistory = {};
if (window.history && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

let onRenderComplete = function initialRenderComplete() {
  const elem = document.getElementById("css");
  if (elem) elem.parentNode.removeChild(elem);
  onRenderComplete = function renderComplete(route, location) {
    document.title = route.title;

    updateMeta("description", route.description);
    // Update necessary tags in <head> at runtime here, ie:
    // updateMeta('keywords', route.keywords);
    // updateCustomMeta('og:url', route.canonicalUrl);
    // updateCustomMeta('og:image', route.imageUrl);
    // updateLink('canonical', route.canonicalUrl);
    // etc.

    let scrollX = 0;
    let scrollY = 0;
    const pos = scrollPositionsHistory[location.key];
    if (pos) {
      scrollX = pos.scrollX;
      scrollY = pos.scrollY;
    } else {
      const targetHash = location.hash.substr(1);
      if (targetHash) {
        const target = document.getElementById(targetHash);
        if (target) {
          scrollY = window.pageYOffset + target.getBoundingClientRect().top;
        }
      }
    }

    // Restore the scroll position if it was saved into the state
    // or scroll to the given #hash anchor
    // or scroll to top of the page
    window.scrollTo(scrollX, scrollY);
  };
};

const container = document.getElementById("app");
let appInstance;
let currentLocation = history.location;

// Re-render the app when window.location changes
async function onLocationChange(location, action) {
  // Remember the latest scroll position for the previous location
  scrollPositionsHistory[currentLocation.key] = {
    scrollX: window.pageXOffset,
    scrollY: window.pageYOffset
  };

  // Delete stored scroll position for next page if any
  if (action === "PUSH") {
    delete scrollPositionsHistory[location.key];
  }
  currentLocation = location;

  try {
    context.pathname = location.pathname;
    context.query = queryString.parse(location.search);

    // Traverses the list of routes in the order they are defined until
    // it finds the first route that matches provided URL path string
    // and whose action method returns anything other than `undefined`.
    const route = await router.resolve({
      ...context,
      path: location.pathname,
      query: queryString.parse(location.search)
    });

    // Prevent multiple page renders during the routing process
    if (currentLocation.key !== location.key) {
      return;
    }

    if (route.redirect) {
      history.replace(route.redirect);
      return;
    }

    appInstance = ReactDOM.render(
      <App context={context}>{route.component}</App>,
      container,
      () => onRenderComplete(route, location)
    );
  } catch (error) {
    if (__DEV__) {
      throw error;
    }

    console.error(error);

    // Do a full page reload if error occurs during client-side navigation
    if (action && currentLocation.key === location.key) {
      window.location.reload();
    }
  }
}

// Handle client-side navigation by using HTML5 History API
// For more information visit https://github.com/mjackson/history#readme
history.listen(onLocationChange);
onLocationChange(currentLocation);

// Enable Hot Module Replacement (HMR)
if (module.hot) {
  module.hot.accept("./utils/router", () => {
    if (appInstance) {
      // Force-update the whole tree, including components that refuse to update
      deepForceUpdate(appInstance);
    }

    onLocationChange(currentLocation);
  });
}



// WEBPACK FOOTER //
// ./src/client.js